import React from 'react';
import PropTypes from 'prop-types';
import * as immutable from 'immutable';
import {List, Map} from 'immutable';
import noop from 'lodash/noop';
import classNames from 'classnames';
import SelectField from 'components/pure/utils/form/SelectField';
import TrashIcon from 'components/pure/utils/icon/TrashIcon';
import NewFilterWordProfile from 'records/admin/topics/NewFilterWordProfile';
import Button from '@material-ui/core/Button';
import Dialog from 'components/pure/utils/Dialog';
import './ProfileKeywordEditor.styl';
import confirm from 'utils/confirm';
import {unsavedMessage} from 'utils/const';
import GenericLink from 'components/GenericLink';
import InputField from 'components/pure/utils/form/InputField';
import NewFilterWord from 'records/admin/topics/NewFilterWord';
import CheckboxField from 'components/pure/utils/form/CheckboxField';

const WHEN_SEARCHED = 'WHEN_SEARCHED';

const DROPDOWN_OPTIONS = List([
  {
    label: 'When Searched For',
    value: WHEN_SEARCHED,
  },
  {
    label: 'When Found',
    value: 'WHEN_FOUND',
  },
  {
    label: 'Always',
    value: 'ALWAYS',
  },
]);

/**
 * Get the option label for a filter word's displayWhen value.
 *
 * @param displayWhen {string} display when enum value from the server.
 * @return {string} the label to use in the dropdown.
 */
function displayWhenLabel(displayWhen) {
  const filtered = DROPDOWN_OPTIONS.filter(option => option.value === displayWhen);
  return filtered.isEmpty() ? '' : filtered.get(0).label;
}

/**
 * Get a validation error message for a keyword.
 *
 * @param keyword {NewFilterWord} the keyword to validate.
 * @param allKeywords {Iterable<NewFilterWord>} all keywords in the profile being edited.
 * @return {string|null} a string error message if the keyword is invalid, or null if it's fine.
 */
function validationMessage(keyword, allKeywords) {
  if (!keyword.filterWordId) {
    console.error('Attempt to validate a word without an id');
  }
  const value = keyword.word;
  if (!value || !value.trim()) {
    return 'Value cannot be blank.';
  }
  const otherWords = allKeywords
    .filterNot(kw => kw.filterWordId === keyword.filterWordId)
    .map(kw => kw.word.trim())
    .toSet();
  if (otherWords.has(value)) {
    return `${value} is already a keyword in this profile.`;
  }
  return null;
}

/**
 * Validate a keyword.
 *
 * @param keyword {NewFilterWord} the keyword to validate.
 * @param allKeywords {Iterable<NewFilterWord>} all keywords in the profile being edited.
 * @param errorMessages {Map<number, string>} current map of filter word id to error message.
 * @return {Map<number, string>} a copy of errorMessages, with any new error message for the
 * given keyword added.
 */
function validate(keyword, allKeywords, errorMessages) {
  const message = validationMessage(keyword, allKeywords);
  if (message) {
    return errorMessages.set(keyword.filterWordId, message);
  } else {
    return errorMessages.delete(keyword.filterWordId);
  }
}

/**
 * Keyword editor. This is a separate dialog that opens when you click on the keywords link in the
 * inline profile editor.
 */
export default class ProfileKeywordEditor extends React.PureComponent {
  static propTypes = {
    editing: PropTypes.bool.isRequired,
    keywords: PropTypes.instanceOf(immutable.List).isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    profile: PropTypes.instanceOf(NewFilterWordProfile).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorMessages: Map(), // Map<FilterWordId, String>
      keywords: List(), // List<NewFilterWord>
      profile: undefined,
      unsavedChanges: false,
    };
  }

  maybeConfirm(onClose) {
    const {unsavedChanges} = this.state;
    if (unsavedChanges) {
      confirm(unsavedMessage).then(onClose, noop);
    } else {
      onClose();
    }
  }

  setDisplayWhen(filterWord, displayWhen) {
    const newFilterWord = filterWord.set('displayWhen', displayWhen);
    this.updateKeyword(newFilterWord);
  }

  setWord(keyword, value) {
    const {errorMessages, keywords} = this.state;
    // We cannot trim here, or the user will be unable to type spaces at all. Instead, we need
    // to trim when validating, then again when saving, to eliminate stray spaces.
    const newFilterWord = keyword.set('word', value);
    this.updateKeyword(newFilterWord, validate(newFilterWord, keywords, errorMessages));
  }

  addKeyword(keyword) {
    const {errorMessages, keywords, profile} = this.state;
    // Prepend so it shows up at the top of the dialog.
    const updatedProfile = profile.prependFilterWord(keyword);
    // Need the prepended word with the temporary ID for validation.
    const updatedKeyword = updatedProfile.filterWords.first();
    this.setState({
      errorMessages: validate(updatedKeyword, keywords, errorMessages),
      keywords: updatedProfile.keywords(),
      profile: updatedProfile,
      unsavedChanges: true,
    });
  }

  removeKeyword(keyword) {
    const {errorMessages, keywords, profile} = this.state;
    this.setState({
      errorMessages: errorMessages.delete(keyword.filterWordId),
      keywords: keywords.delete(keywords.findIndex(w => w.filterWordId === keyword.filterWordId)),
      profile: profile.removeFilterWordById(keyword.filterWordId),
      unsavedChanges: true,
    });
  }

  updateKeyword(keyword, optionalErrorMessages) {
    const {errorMessages, keywords, profile} = this.state;
    this.setState({
      errorMessages: optionalErrorMessages || errorMessages,
      keywords: keywords.set(
        keywords.findIndex(w => w.filterWordId === keyword.filterWordId),
        keyword
      ),
      profile: profile.updateFilterWord(keyword),
      unsavedChanges: true,
    });
  }

  cancel(onCancel) {
    const {originalKeywords, originalProfile} = this.state;
    this.setState({
      keywords: originalKeywords,
      profile: originalProfile,
    });
    onCancel();
  }

  setup() {
    // Copy from props to state when opened so we can edit w/o changing global state (which makes
    // cancel / close easy).
    const {keywords, profile} = this.props;
    this.setState({
      originalKeywords: keywords,
      originalProfile: profile,
      keywords,
      profile,
    });
  }

  componentDidMount() {
    this.setup();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.profile !== this.props.profile) {
      this.setup();
    }
  }

  render() {
    const {editing, open, onClose, onSave} = this.props;
    const {errorMessages, keywords} = this.state;
    const actions = editing
      ? [
          <Button
            variant="contained"
            color="primary"
            className="ev-button"
            disabled={!errorMessages.isEmpty()}
            onClick={() => {
              onClose();
              onSave(keywords);
            }}>
            Save
          </Button>,
          <Button variant="outlined" className="ev-button" onClick={() => this.cancel(onClose)}>
            Cancel
          </Button>,
        ]
      : [];
    return (
      <Dialog
        onRequestClose={onClose}
        open={open}
        closeButton
        title="Words/Phrases"
        actions={actions}>
        <div className="profile-keyword-editor">
          <div className="help-text">
            Enter keywords for this profile. If you have a set of related words/phrases, create a
            topic instead.
          </div>
          <div className="profile-keyword-header">
            <div className="keyword">Keywords</div>
            <div className="show-in-dropdown">Show in Dropdown?</div>
            <div className="train-into-transcript">Train Into Transcript Engine?</div>
            {editing && <div className="remove">Remove</div>}
          </div>
          <div className="new-keyword">
            {editing && (
              <GenericLink
                onClick={() =>
                  // empty string for word is invalid, but keeps the input "controlled", as opposed to the
                  // record's default undefined value.
                  // https://stackoverflow.com/a/47012342
                  this.addKeyword(
                    new NewFilterWord({
                      word: '',
                      topic: false,
                      displayWhen: WHEN_SEARCHED,
                    })
                  )
                }>
                Add New Row
              </GenericLink>
            )}
          </div>
          <div className="profile-keyword-data">
            {keywords.map(keyword => (
              <div
                key={keyword.filterWordId}
                className={classNames('keyword-row', {viewing: !editing})}>
                <div className="keyword">
                  {editing ? (
                    <div>
                      <InputField
                        className={classNames({
                          invalid: errorMessages.get(keyword.filterWordId),
                        })}
                        value={keyword.word}
                        onChange={value => this.setWord(keyword, value)}
                      />
                      <div className="error-message">{errorMessages.get(keyword.filterWordId)}</div>
                    </div>
                  ) : (
                    keyword.word
                  )}
                </div>
                <div className="show-in-dropdown">
                  {editing ? (
                    <SelectField
                      options={DROPDOWN_OPTIONS}
                      value={keyword.displayWhen}
                      onChange={value => this.setDisplayWhen(keyword, value)}
                      hideResetButton
                    />
                  ) : (
                    <div>{displayWhenLabel(keyword.displayWhen)}</div>
                  )}
                </div>
                <div className="train-into-transcript">
                  <CheckboxField
                    checked={keyword.trainIntoTranscript}
                    disabled={!editing}
                    onCheck={() =>
                      this.updateKeyword(
                        keyword.set('trainIntoTranscript', !keyword.trainIntoTranscript)
                      )
                    }
                  />
                </div>
                {editing && (
                  <div className="remove">
                    <TrashIcon onClick={() => this.removeKeyword(keyword)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    );
  }
}
